import * as React from 'react';
import { Box, Checkbox, Dialog, DialogTitle, DialogContent, Stack, DialogActions, 
    Button, List, ListItem, ListItemText, Typography, Tabs, Tab, TextField} from '@mui/material';

import {
    GridActionsCellItem
  } from '@mui/x-data-grid';
import {default as DataGrid} from '../../components/LocalizedDataGrid';

import ZoomInIcon from '@mui/icons-material/ZoomIn';
import DoneIcon from '@mui/icons-material/Done';

import { useTranslation } from "react-i18next";
import { ApiContext } from '../../App';



const DetailsDialog = (props) => {

    const {open, onClose, data} = props;

    const { t } = useTranslation();

    return (
        <Dialog open={open} onClose={()=>onClose()}>
            <DialogTitle>
            {t("Details")}
            </DialogTitle>
            <DialogContent>
                <Typography variant="h6" color="primary">Alert:</Typography>
                <List>
                    <ListItem disablePadding>
                        <ListItemText primary={t("Name")} secondary={data.name}/>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemText primary={t("Priority")} secondary={data.priority}/>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemText primary={t("Message")} secondary={data.message}/>
                    </ListItem>
                </List>
                <Typography variant="h6" color="primary">Input:</Typography>
                <List>
                    <ListItem disablePadding>
                        <ListItemText primary={t("Controller")} secondary={data.controller_name}/>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemText primary={t("ID")} secondary={data.point_identifier}/>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemText primary={t("Location")} secondary={data.point_location}/>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemText primary={t("System")} secondary={data.point_system}/>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemText primary={t("Device")} secondary={data.point_name}/>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemText primary={t("Key")} secondary={data.point_key}/>
                    </ListItem>
                </List>
                <Typography variant="h6" color="primary">{t("Acknowledgement")}:</Typography>
                {
                    data.acknowledged?
                    <List>
                        <ListItem disablePadding>
                            <ListItemText primary={t("Comments")} secondary={data.comments}/>
                        </ListItem>
                        <ListItem disablePadding>
                            <ListItemText primary={t("User")} secondary={data.acknowledged_by_username}/>
                        </ListItem>
                    </List>
                    :
                    <Typography>{t("Not acknowledged")}</Typography>
                }  
            </DialogContent>
            <DialogActions>
            <Button onClick={()=>onClose()} autoFocus>
                {t("OK")}
            </Button>
            </DialogActions>
        </Dialog>
    )
}

const AcknowledgementPopUp = (props) => {

    const {onAcknowledge, onClose, open, comments, onCommentsChange} = props;

    const { t } = useTranslation();


    return (
        <Dialog open={open} onClose={()=>onClose()} fullWidth>
            <DialogTitle>
            {t("Acknowledge alert")}
            </DialogTitle>
            <DialogContent>
                <TextField
                    variant="standard"
                    id="comments"
                    label={t("Comments")}
                    value={comments ?? ""}
                    onChange={(event)=>onCommentsChange(event.target.value)}
                    fullWidth
                    multiline
                    inputProps={{ maxLength: 1500 }}
                    />
            </DialogContent>
            <DialogActions>
                <Button onClick={()=>onAcknowledge()}>
                    {t("Yes")}
                </Button>
                <Button onClick={()=>onClose()} autoFocus>
                    {t("Cancel")}
                </Button>
            </DialogActions>
        </Dialog>
    )

}


export default function AlarmViewer(props) {

    const { facility} = props;

    const { t } = useTranslation();

    const [rows, setRows] = React.useState([]);

    const [detailsDialogOpen, setDetailsDialogOpen] = React.useState(false);
    const [detailsDialogRowID, setDetailsDialogRowID] = React.useState("");
    const [activeTab, setActiveTab] = React.useState("active");
    const [acknowledgementPopUpOpen, setAcknowledgementPopUpOpen] = React.useState(false);
    const [acknowledgementCandidateID, setAcknowledgementCandidateID] = React.useState("");
    const [acknowledgementComments, setAcknowledgementComments] = React.useState("");
    const {alarmsApi} = React.useContext(ApiContext)

    React.useEffect(() => {

        alarmsApi.list(
            facility,
            data => setRows(data),
            ()=>{
              alert(t("Couldn't connect to server."));
            }
          )
    },[facility])


    const handleDetailsClick = (id) => {
        setDetailsDialogRowID(id);
        setDetailsDialogOpen(true);
    }

    const handleAcknowledgeClick = (id) => {
        setAcknowledgementCandidateID(id);
        setAcknowledgementPopUpOpen(true);
    }

    const handleAcknowledge = () => {

        const onSuccess = (data) => {
            setAcknowledgementPopUpOpen(false);
            setRows(rows => rows.map((item) => item.url !== acknowledgementCandidateID? item : {...item, acknowledged: true, comments: acknowledgementComments}))
        }

        const onError = () => {
            alert(t("Failed to acknowledge alert."))
        }

        alarmsApi.acknowledgeAlarm(acknowledgementCandidateID, acknowledgementComments, onSuccess, onError)

    }


    const columns = [
        { 
            field: 'timestamp', 
            headerName: t('Start time'), 
            minWidth: 200,
            valueFormatter: (v)=> (new Date(v.value)).toLocaleString()
        },
        { 
            field: 'end_timestamp', 
            headerName: t('End time'), 
            minWidth: 200,
            valueGetter: (v) => v.value ? (new Date(v.value)).toLocaleString() : ""
        },
        { 
            field: 'priority', 
            headerName: t('Priority'), 
            minWidth: 120
        },
        { 
            field: 'name', 
            headerName: t('Alert'), 
            minWidth: 250,
            flex: 1
        },
        { 
            field: 'point_location', 
            headerName: t('Location'), 
            minWidth: 180,
            flex: 0.5
        },
        { 
            field: 'acknowledged', 
            headerName: t('Acknowledge'), 
            minWidth: 110,
            renderCell: (params) => {
                return (
                    params.value? <DoneIcon/> : <Checkbox checked={params.value} onChange={()=>handleAcknowledgeClick(params.id)}/>
                )
            },
            align: "center"
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: t('Details'),
            minWidth: 50,
            cellClassName: 'actions',
            getActions: ({ id }) => {
              return [
                <GridActionsCellItem
                  icon={<ZoomInIcon />}
                  label={t("Details")}
                  className="textPrimary"
                  onClick={() => handleDetailsClick(id)}
                  color="inherit"
                />,
              ];
            },
          },

    ]


    return (
        <Stack>
            <DetailsDialog 
                open={detailsDialogOpen} 
                onClose={()=>setDetailsDialogOpen(false)}
                data={rows.find((row)=>row.url === detailsDialogRowID) ?? {}}
            />
            <AcknowledgementPopUp
                open={acknowledgementPopUpOpen}
                onClose={()=>setAcknowledgementPopUpOpen(false)}
                onAcknowledge={()=>handleAcknowledge()}
                comments={acknowledgementComments}
                onCommentsChange={(value)=>setAcknowledgementComments(value)}
            />
            <Tabs value={activeTab} onChange={(event, newValue)=>setActiveTab(newValue)}>
                    <Tab label={t("Active")} value="active" />
                    <Tab label={t("Archived")} value="archived"/>
                </Tabs>
            <Box
            sx={{
                p:3,
                height: 'calc(100vh - 120px)',
                width: '100%'
            }}>
                <DataGrid
                    rows={activeTab === "active"? rows.filter((item)=>!item.archived) : rows.filter((item)=>item.archived)}
                    columns={columns}
                    getRowId={(row) => row.url}
                />
            </Box>
        </Stack>
    )

};



